// src/pages/Orders/index.jsx
import React from "react";
import OrdersFilter from "@components/orders/OrdersFilter";
import OrdersTable, { pickupLabel, dropoffLabel, storeNameOf } from "@components/orders/OrdersTable";
import Loader from "@components/Loader";
import { useOrdersQuery, useSendEinvoiceBulk } from "@queries/order";
import { useSearchParams } from "react-router-dom";
import "@styles/opencart-admin.css";

const toApiParams = (f) => ({
  order_id: f.orderId || undefined,
  customer_id: f.customerId || undefined,
  invoice_no: f.invoiceNo || undefined,
  type: f.orderType || undefined,
  order_status_id: f.orderStatus || undefined,
  store_id: f.storeId || undefined,
  unpaid: f.unpaidOnly ? 1 : undefined,
  reschedule: f.rescheduleOnly ? 1 : undefined,
  first_time: f.firstTimeOnly ? 1 : undefined,
  out_source: f.outSourceOnly ? 1 : undefined,
  date_from: f.dateFrom || undefined,
  date_to: f.dateTo || undefined,
});

const fromSearch = (sp) => ({
  orderId: sp.get("order_id") || "",
  customerId: sp.get("customer_id") || "",
  invoiceNo: sp.get("invoice_no") || "",
  orderType: sp.get("type") || "",
  orderStatus: sp.get("order_status_id") || sp.get("status") || "",
  storeId: sp.get("store_id") || "",
  unpaidOnly: sp.get("unpaid") === "1",
  rescheduleOnly: sp.get("reschedule") === "1",
  firstTimeOnly: sp.get("first_time") === "1",
  outSourceOnly: sp.get("out_source") === "1",
  dateFrom: sp.get("date_from") || "",
  dateTo: sp.get("date_to") || "",
});

const orderIdOf = (o) => String(o?.order_id ?? o?.id ?? "");

const isEinvoiceSuccess = (r) => {
  if (!r) return false;
  if (r.api_status === "PASS") return true;
  if (r.action === "cached" && r.reason === "already_success") return true;
  return false;
};

export default function OrdersPage() {
  const [search, setSearch] = useSearchParams();
  
  const filters = React.useMemo(() => fromSearch(search), [search]);
  const page = Math.max(1, parseInt(search.get("page") || "1", 10));
  const pageSize = parseInt(search.get("page_size") || "100", 10);

  // Ensure default params are in URL if missing
  React.useEffect(() => {
    if (!search.has("page") || !search.has("page_size")) {
      const next = new URLSearchParams(search);
      if (!next.has("page")) next.set("page", "1");
      if (!next.has("page_size")) next.set("page_size", "100");
      setSearch(next, { replace: true });
    }
  }, [search, setSearch]);

  const handleFilterChange = (newFilters) => {
    const next = new URLSearchParams();
    const api = toApiParams(newFilters);
    Object.entries(api).forEach(([k, v]) => v != null && next.set(k, String(v)));
    next.set("page", "1");
    next.set("page_size", String(pageSize || 100));
    setSearch(next, { replace: true });
  };

  const handlePageChange = (newPage) => {
    const next = new URLSearchParams(search);
    next.set("page", String(newPage));
    next.set("page_size", String(pageSize || 100));
    setSearch(next, { replace: true });
  };

  const { data, isLoading, isError, error, isFetching } =
    useOrdersQuery(filters, page, pageSize);

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const pageOrderIds = React.useMemo(
    () => rows.map(orderIdOf).filter(Boolean),
    [rows]
  );

  const [selected, setSelected] = React.useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const [jf, setJf] = React.useState(() => ({
    running: false,
    total: 0,
    done: 0,
    processingOrderId: "",
    statusById: {},
    lastSummary: null,
  }));

  const sendBulk = useSendEinvoiceBulk();

  React.useEffect(() => {
    setSelected(new Set());
  }, [page, filters]);

  const selectedCount = selected.size;

  const toggleOne = (id) => {
    const sid = String(id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) pageOrderIds.forEach((id) => next.add(String(id)));
      else pageOrderIds.forEach((id) => next.delete(String(id)));
      return next;
    });
  };

  const isAllOnPageSelected =
    pageOrderIds.length > 0 && pageOrderIds.every((id) => selected.has(String(id)));
  const isSomeOnPageSelected =
    pageOrderIds.some((id) => selected.has(String(id))) && !isAllOnPageSelected;

  const openConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => (!jf.running ? setConfirmOpen(false) : null);

  const exportCsv = () => {
    if (!rows.length) return;

    const headers = [
      "order_id",
      "customer",
      "store_name",
      "status",
      "total",
      "date_added",
      "type",
      "pickup",
      "dropoff",
      "jofotara",
    ];

    const escapeCsv = (v) => {
      const s = String(v ?? "");
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const getJoFotaraText = (oid) => {
      const st = jf.statusById?.[String(oid)]?.state;
      if (st === "pass") return "Sent";
      if (st === "fail") return "Failed";
      if (st === "processing") return "Sending";
      return "";
    };

    const lines = [];
    lines.push(headers.join(","));

    const rowsToExport = selected.size > 0 ? rows.filter(o => selected.has(orderIdOf(o))) : rows;

    for (const o of rowsToExport) {
      const oid = orderIdOf(o);
      const customer = o.customer?.name ?? o.name ?? "";
      const status = o.status_name ?? o.status ?? "";
      const totalVal = o.total;
      const dateAdded = o.date ?? o.date_added ?? "";
      const type = o.type ?? o.order_type ?? "";
      const pickup = pickupLabel(o);
      const dropoff = dropoffLabel(o);
      const jof = getJoFotaraText(oid);

      const row = [
        oid,
        customer,
        storeNameOf(o),
        status,
        totalVal,
        dateAdded,
        type,
        pickup,
        dropoff,
        jof,
      ].map(escapeCsv);

      lines.push(row.join(","));
    }

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${new Date().toISOString().slice(0, 10)}_page_${page}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const setOnlyOneProcessing = (ids, processingId) => {
    setJf((p) => {
      const next = { ...p.statusById };
      for (const id of ids) {
        if (String(id) === String(processingId)) {
          next[String(id)] = { state: "processing", message: "Sending…" };
        } else if (next[String(id)]?.state === "processing") {
          next[String(id)] = { state: "idle", message: "" };
        }
      }
      return { ...p, processingOrderId: String(processingId), statusById: next };
    });
  };

  const sendInBackground = () => {
    const ids = Array.from(selected).map(String).filter(Boolean);
    if (!ids.length) return;

    setConfirmOpen(false);

    setJf((p) => ({
      ...p,
      running: true,
      total: ids.length,
      done: 0,
      processingOrderId: ids[0] || "",
      lastSummary: null,
      statusById: { ...p.statusById },
    }));

    setOnlyOneProcessing(ids, ids[0]);

    const startedAt = Date.now();

    (async () => {
      try {
        const res = await sendBulk.mutateAsync({
          selected: ids.map((x) => Number(x)),
          select_all: false,
          no_tax: false,
        });

        const results = Array.isArray(res?.results) ? res.results : [];
        const counts = res?.counts || null;

        const patch = {};
        let pass = 0;
        let fail = 0;

        for (const id of ids) {
          const row = results.find((r) => String(r.order_id) === String(id)) || null;

          const ok = isEinvoiceSuccess(row);
          if (ok) pass += 1;
          else fail += 1;

          let msg = "Failed";
          if (ok) msg = row?.action === "cached" ? "Already sent (cached)" : "Sent to JoFotara";
          else if (row?.api_status) msg = `Failed: ${row.api_status}`;
          else if (row?.reason) msg = `Failed: ${row.reason}`;

          patch[String(id)] = {
            state: ok ? "pass" : "fail",
            message: msg,
          };
        }

        setJf((p) => ({
          ...p,
          statusById: { ...p.statusById, ...patch },
          done: ids.length,
          running: false,
          processingOrderId: "",
          lastSummary: {
            pass: counts?.success ?? pass,
            fail: counts ? (counts.failed + counts.not_found) : fail,
            tookMs: Date.now() - startedAt,
          },
        }));
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed";
        setJf((p) => {
          const next = { ...p.statusById };
          for (const id of ids) next[String(id)] = { state: "fail", message: `Failed: ${msg}` };
          return {
            ...p,
            statusById: next,
            done: ids.length,
            running: false,
            processingOrderId: "",
            lastSummary: { pass: 0, fail: ids.length, tookMs: Date.now() - startedAt },
          };
        });
      }
    })();
  };

  const pct = Math.round((jf.done / Math.max(1, jf.total)) * 100);

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Orders</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Orders</span>
        </nav>
      </div>

      <div className="oc-card oc-filter">
        <div className="title">Filter</div>
        <OrdersFilter
          value={filters}
          onChange={handleFilterChange}
        />
      </div>

      {isError && (
        <div className="oc-card" style={{ color: "#c00" }}>
          Error: {error.message}
        </div>
      )}

      <div className="oc-card">
        <div className="oc-toolbar" style={{ gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <strong>Order List</strong>
            <span className="oc-muted">
              Selected: <strong>{selectedCount}</strong>
            </span>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>

            <button
              className={`oc-btn primary ${jf.running ? "loading" : ""}`}
              type="button"
              onClick={openConfirm}
              disabled={rows.length === 0 || jf.running}
            >
              {jf.running ? "Sending…" : "Send To jofotara"}
            </button>

            <button
              className="oc-btn ghost"
              type="button"
              onClick={exportCsv}
              disabled={rows.length === 0}
            >
              export
            </button>
          </div>
        </div>

        {jf.running && (
          <div className="oc-progress-card">
            <div className="oc-progress-top">
              <div>
                <strong>Sending invoices…</strong>
                <div className="oc-muted" style={{ marginTop: 2 }}>
                  {jf.done}/{jf.total}
                  {jf.processingOrderId ? ` • Processing: ${jf.processingOrderId}` : ""}
                </div>
              </div>
              <div className="oc-muted">{pct}%</div>
            </div>

            <div className="oc-progress-track" aria-hidden="true">
              <div className="oc-progress-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {!jf.running && jf.lastSummary && (
          <div className="oc-alert success" style={{ marginTop: 12 }}>
            Sent finished: <strong>{jf.lastSummary.pass}</strong> PASS,{" "}
            <strong>{jf.lastSummary.fail}</strong> FAIL •{" "}
            {Math.round(jf.lastSummary.tookMs / 1000)}s
          </div>
        )}

        <OrdersTable
          rows={rows}
          selected={selected}
          isAllOnPageSelected={isAllOnPageSelected}
          isSomeOnPageSelected={isSomeOnPageSelected}
          onToggleOne={toggleOne}
          onToggleAllOnPage={toggleAllOnPage}
          jofotaraStatusById={jf.statusById}
          processingOrderId={jf.processingOrderId}
        />
      </div>

      <div
        className="oc-card"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> ({total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => handlePageChange(1)} type="button">
            « First
          </button>
          <button className="oc-btn ghost" disabled={page <= 1} onClick={() => handlePageChange(page - 1)} type="button">
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => handlePageChange(page + 1)} type="button">
            Next ›
          </button>
          <button className="oc-btn ghost" disabled={page >= pages} onClick={() => handlePageChange(pages)} type="button">
            Last »
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="oc-modal-backdrop" onMouseDown={closeConfirm}>
          <div
            className="oc-modal"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="oc-modal-head">
              <strong>Send To JoFotara</strong>
              <button
                className="oc-ico-btn"
                type="button"
                onClick={closeConfirm}
                disabled={jf.running}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="oc-modal-body">
              <div className="oc-muted">
                This will call the backend API and send e-invoices for selected orders.
              </div>

              <div className="oc-modal-kpis">
                <div className="oc-kpi">
                  <div className="k">{selectedCount}</div>
                  <div className="l">Selected</div>
                </div>
                <div className="oc-kpi">
                  <div className="k">{rows.length}</div>
                  <div className="l">Orders on page</div>
                </div>
              </div>

              {selectedCount === 0 && (
                <div className="oc-alert warn" style={{ marginTop: 12 }}>
                  No orders selected. Use “Select all (page)” or select specific rows.
                </div>
              )}
            </div>

            <div className="oc-modal-foot">
              <button
                className="oc-btn ghost"
                type="button"
                onClick={() => toggleAllOnPage(true)}
                disabled={jf.running}
              >
                Select all (page)
              </button>
              <div style={{ flex: 1 }} />
              <button className="oc-btn ghost" type="button" onClick={closeConfirm} disabled={jf.running}>
                Cancel
              </button>
              <button
                className="oc-btn primary"
                type="button"
                onClick={sendInBackground}
                disabled={jf.running || selectedCount === 0}
              >
                Confirm & Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

